/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import StringUtil from './string/string-util';

/**
 * @private
 * @typedef {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9} Digit
 * @typedef {`${0 | 1}${Digit}` | '20' | '21' | '22' | '23'} Hour
 * @typedef {`${0 | 1 | 2 | 3 | 4 | 5}${Digit}`} Sixty
 * @typedef {Sixty} Minute
 * @typedef {Sixty} Second
 */

/**
 * ISO 8601 reprezentace lokálního času s granularitou na sekundy ve formátu
 * HH:mm:ss (lexikograficky porovnatelný).
 * @typedef {`${Hour}:${Minute}:${Second}`} ISOTimeString
 */

/**
 * Maximum je 23:59:59 a pokud po přičtení se dostane hodnota cez hranici,
 * spraví sa modulo 24 hodín.
 * @param {ISOTimeString} time
 * @param {number} msToAdd
 * @returns {ISOTimeString}
 */
export function addMsModulo(time, msToAdd) {
  const totalMs = (getMillisecondsOfDay(time) + msToAdd) % (24 * 3600 * 1000);

  const hour = Math.floor(totalMs / 3600000);
  const minute = Math.floor((totalMs / 60000) % 60);
  const second = Math.floor((totalMs / 1000) % 60);

  return fromTimeParts(hour, minute, second);
}

/**
 * Porovná dvě {@link ISOTimeString} hodnoty.
 * @see {@link Array.prototype.sort}
 * @see {@link Array.prototype.toSorted}
 * @param {ISOTimeString} time1
 * @param {ISOTimeString} time2
 * @returns {number} Vrátí negativní číslo, pokud je první argument menší jak
 * druhý argument, nulu pokud jsou stejné, jinak pozitivní číslo.
 */
export function compare(time1, time2) {
  // Využívá toho, že ISOTimeString pevný formát je lexikograficky porovnatelný.
  return StringUtil.compare(time1, time2);
}

/**
 * @param {ISOTimeString} time
 * @returns {[hour: number, minute: number, second: number]} Vrátí pole částí času.
 */
export function extractTimeParts(time) {
  return /** @type {ReturnType<typeof extractTimeParts>} */ (
    time.split(':').map((part) => parseInt(part, 10))
  );
}

/**
 * @param {number} hour
 * @param {number} minute
 * @param {number} second
 * @returns {ISOTimeString}
 */
export function fromTimeParts(hour, minute, second) {
  return /** @type {ReturnType<typeof fromTimeParts>} */ (
    `${pad(hour)}:${pad(minute)}:${pad(second)}`
  );
}

/**
 * Zjistí, zda je hodnota {@link ISOTimeString}.
 * @param {any} value
 * @returns {value is ISOTimeString}
 */
export function isISOTimeString(value) {
  return /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(value);
}

/**
 * Zjistí, zda je první čas menší než druhý čas.
 * @param {ISOTimeString} time1
 * @param {ISOTimeString} time2
 * @returns {boolean}
 */
export function isLess(time1, time2) {
  return StringUtil.isLess(time1, time2);
}

/**
 * @param {ISOTimeString} time
 * @returns {number}
 */
export function getMillisecondsOfDay(time) {
  const [hour, minute, second] = extractTimeParts(time);
  return hour * 3600 * 1000 + minute * 60 * 1000 + second * 1000;
}

/**
 * Vrátí větší čas mezi dvěma {@link ISOTimeString} hodnotami.
 * @param {ISOTimeString} time1
 * @param {ISOTimeString} time2
 * @returns {ISOTimeString} Vrátí větší z časů time1 a time2.
 */
export function max(time1, time2) {
  return isLess(time1, time2) ? time2 : time1;
}

/**
 * @param {number} num
 * @returns {string}
 */
function pad(num) {
  return num.toString().padStart(2, '0');
}
